import { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CardContainer from "../../../../components/Card/CardContainer";
import CardMedia from "../../../../components/Media/CardMedia";
import ModalMedia from "../../../../components/Modal/ModalMedia";
import UploadFileButton from "../../../../components/Buttons/UploadFileButton";
import SectionHeader from "./SectionHeader";
import ModalAdditionalAsset from "./ModalAdditionalAsset";
import { ConfirmAlert } from "../../../../components/Alerts/Alerts";
import { useDispatchApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  SECTION_ADDITIONAL_ASSETS_TITLE,
  SECTION_ADDITIONAL_ASSETS_SUBTITLE,
  SECTION_ADDITIONAL_ASSETS_ASSET_LABEL,
  SECTION_ADDITIONAL_ASSETS_DELETE_CONFIRM,
  SECTION_ADDITIONAL_ASSETS_DELETE_CONFIRM_TITLE,
  BUTTON_DELETE,
  BUTTON_CANCEL,
} from "../../../../i18n/keysTranslations";
import { LOGO_IMAGE_FORMATS, BORDER_RADIUS } from "../../../../utils/constants";
import {
  BORDER_GRAY_COLOR,
  INPUT_COLOR_BORDER,
  SECONDARY_COLOR_TEXT,
} from "../../../../utils/colors";
import {
  MAX_ADDITIONAL_ASSETS,
  collectUniqueVariantGroups,
} from "../utils/formDataTransformers";
import { useBrandSetupReadOnly } from "../context/brandSetupReadOnlyContext";

const UPLOAD_INPUT_ID = "upload-additional-assets";
const THUMB_SIZE = 56;
const NEW_ASSET_INDEX = "new";

const AdditionalAssetsEmptyState = ({
  onUploadClick,
  readOnly = false,
  isUploading = false,
}) => {
  return (
    <Box
      onClick={readOnly || isUploading ? undefined : onUploadClick}
      sx={{
        height: 160,
        width: 160,
        maxWidth: "100%",
        borderRadius: 2,
        border: `2px dashed ${INPUT_COLOR_BORDER}`,
        backgroundColor: "action.hover",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: readOnly || isUploading ? "default" : "pointer",
        transition: "all 0.2s",
        ...(!readOnly &&
          !isUploading && {
            "&:hover": {
              borderColor: "text.primary",
              backgroundColor: "action.selected",
            },
          }),
      }}
    >
      {isUploading ? (
        <CircularProgress size={40} />
      ) : (
        <AddIcon
          sx={{ fontSize: 40, color: "text.secondary !important" }}
          htmlColor={SECONDARY_COLOR_TEXT}
        />
      )}
    </Box>
  );
};

const AdditionalAssetAddSlot = ({ onClick, isUploading = false }) => {
  return (
    <Box
      onClick={isUploading ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 70,
        borderRadius: BORDER_RADIUS,
        border: `2px dashed ${INPUT_COLOR_BORDER}`,
        backgroundColor: "action.hover",
        cursor: isUploading ? "default" : "pointer",
        transition: "all 0.2s",
        ...(!isUploading && {
          "&:hover": {
            borderColor: "text.primary",
            backgroundColor: "action.selected",
          },
        }),
      }}
    >
      {isUploading ? (
        <CircularProgress size={32} />
      ) : (
        <AddIcon
          sx={{ fontSize: 32, color: "text.secondary !important" }}
          htmlColor={SECONDARY_COLOR_TEXT}
        />
      )}
    </Box>
  );
};

const AdditionalAssetCard = ({
  asset,
  index,
  assetLabel,
  readOnly = false,
  onEdit,
  onDelete,
  onPreviewImage,
}) => {
  const { t } = useTranslationApp();

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${BORDER_GRAY_COLOR}`,
        borderColor: INPUT_COLOR_BORDER,
        bgcolor: "background.paper",
        alignItems: "center",
        px: 2,
        py: 2,
        cursor: readOnly ? "default" : "pointer",
      }}
      onClick={() => {
        if (readOnly) {
          onPreviewImage(asset.urlImage);
          return;
        }
        onEdit(index);
      }}
    >
      <CardMedia
        url={asset.urlImage}
        onClick={(event) => {
          event.stopPropagation();
          onPreviewImage(asset.urlImage);
        }}
        sx={{
          flexShrink: 0,
          cursor: "pointer",
          objectFit: "contain",
          objectPosition: "center",
          height: THUMB_SIZE,
          width: THUMB_SIZE,
          borderRadius: 1.5,
          backgroundColor: "action.hover",
          "&:hover": {
            filter: "brightness(0.85)",
          },
        }}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: 0.2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: "16px", fontWeight: 500 }}
          >
            {assetLabel}
          </Typography>

          {!readOnly && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(index);
                }}
                aria-label={`Editar ${assetLabel}`}
                sx={{
                  p: 0.5,
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <EditIcon sx={{ fontSize: 17 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(index);
                }}
                aria-label={`${t(BUTTON_DELETE)} ${assetLabel}`}
                sx={{
                  p: 0.5,
                  color: "text.secondary",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <CloseIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Box>
          )}
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            fontSize: "14px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {asset.prompt}
        </Typography>
      </Box>
    </Box>
  );
};

export const AdditionalAssetsSection = ({ formMethods, hasError = false }) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());
  const readOnly = useBrandSetupReadOnly();
  const hasAdvancedConfigurationAdditionalAssets =
    formMethods?.hasAdvancedConfigurationAdditionalAssets === true;
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [selectedAssetIndex, setSelectedAssetIndex] = useState(null);
  const [pendingUrlImage, setPendingUrlImage] = useState("");
  const [localAsset, setLocalAsset] = useState({
    urlImage: "",
    prompt: "",
    isConditional: false,
    variableInfo: false,
    variantGroup: "",
  });

  const watchedAdditionalAssets = formMethods?.watch?.("additionalAssets");
  const additionalAssets = useMemo(
    () =>
      Array.isArray(watchedAdditionalAssets) ? watchedAdditionalAssets : [],
    [watchedAdditionalAssets]
  );
  const assetCount = Array.isArray(additionalAssets)
    ? additionalAssets.length
    : 0;
  const isMaxReached = assetCount >= MAX_ADDITIONAL_ASSETS;
  const variantGroupOptions = useMemo(() => {
    const excludeIndex =
      typeof selectedAssetIndex === "number" && selectedAssetIndex >= 0
        ? selectedAssetIndex
        : null;

    return collectUniqueVariantGroups(additionalAssets, { excludeIndex });
  }, [additionalAssets, selectedAssetIndex]);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const {
    appendAdditionalAsset = () => {},
    removeAdditionalAsset = () => {},
    updateAdditionalAsset = () => {},
  } = formMethods;

  const triggerUpload = () => {
    if (readOnly || isUploading) return;
    if (!isModalOpen && isMaxReached) return;
    const input = document.getElementById(UPLOAD_INPUT_ID);
    if (input) {
      input.click();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAssetIndex(null);
    setPendingUrlImage("");
    setLocalAsset({
      urlImage: "",
      prompt: "",
      isConditional: false,
      variableInfo: false,
      variantGroup: "",
    });
  };

  const handleOpenEditModal = (index) => {
    const asset = additionalAssets[index];
    if (!asset) return;
    setSelectedAssetIndex(index);
    setPendingUrlImage("");
    setLocalAsset({
      urlImage: asset.urlImage || "",
      prompt: asset.prompt || "",
      isConditional: !!asset.isConditional,
      variableInfo: !!asset.variableInfo,
      variantGroup: asset.variantGroup || "",
    });
    setIsModalOpen(true);
  };

  const onImageUpload = (newUrls = []) => {
    const url = newUrls?.[0];
    if (!url?.trim()) return;

    if (isModalOpen) {
      setLocalAsset((prev) => ({ ...prev, urlImage: url }));
      setPendingUrlImage(url);
      return;
    }

    if (isMaxReached) return;

    setPendingUrlImage(url);
    setSelectedAssetIndex(NEW_ASSET_INDEX);
    setLocalAsset({
      urlImage: url,
      prompt: "",
      isConditional: false,
      variableInfo: false,
      variantGroup: "",
    });
    setIsModalOpen(true);
  };

  const handleSaveAsset = ({
    urlImage,
    prompt,
    isConditional = false,
    variableInfo = false,
    variantGroup = "",
  }) => {
    if (!urlImage?.trim() || !prompt?.trim()) return;

    const asset = {
      urlImage: urlImage.trim(),
      prompt: prompt.trim(),
      isConditional: hasAdvancedConfigurationAdditionalAssets
        ? !!isConditional
        : false,
      variableInfo: hasAdvancedConfigurationAdditionalAssets
        ? !!variableInfo
        : false,
      variantGroup: hasAdvancedConfigurationAdditionalAssets
        ? (variantGroup || "").trim()
        : "",
    };

    if (selectedAssetIndex === NEW_ASSET_INDEX) {
      appendAdditionalAsset(asset);
    } else if (
      typeof selectedAssetIndex === "number" &&
      selectedAssetIndex >= 0
    ) {
      updateAdditionalAsset(selectedAssetIndex, asset);
    }

    handleCloseModal();
  };

  const handleDeleteAsset = async (index) => {
    const asset = additionalAssets[index];
    if (!asset) return;

    if (asset.prompt?.trim()) {
      const result = await ConfirmAlert({
        title: t(SECTION_ADDITIONAL_ASSETS_DELETE_CONFIRM_TITLE),
        text: t(SECTION_ADDITIONAL_ASSETS_DELETE_CONFIRM),
        confirmButtonText: t(BUTTON_DELETE),
        cancelButtonText: t(BUTTON_CANCEL),
      });
      if (!result.isConfirmed) return;
    }

    removeAdditionalAsset(index);
  };

  const onOpenPreviewModal = (url) => {
    if (!url) return;
    setPreviewImageUrl(url);
    setIsPreviewModalOpen(true);
  };

  const onClosePreviewModal = () => {
    setIsPreviewModalOpen(false);
    setPreviewImageUrl(null);
  };

  const getAssetLabel = (index) =>
    t(SECTION_ADDITIONAL_ASSETS_ASSET_LABEL, { number: index + 1 });

  return (
    <>
      <CardContainer title="" hasError={hasError}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: "100%",
            width: "100%",
          }}
        >
          <SectionHeader
            icon={CollectionsBookmarkOutlinedIcon}
            title={SECTION_ADDITIONAL_ASSETS_TITLE}
            subtitle={SECTION_ADDITIONAL_ASSETS_SUBTITLE}
            actionButton={!readOnly}
            actionButtonDisabled={isMaxReached || isUploading}
            onActionButtonClick={triggerUpload}
            actionButtonIcon={AddIcon}
          />

          {assetCount === 0 ? (
            <AdditionalAssetsEmptyState
              onUploadClick={triggerUpload}
              readOnly={readOnly}
              isUploading={isUploading}
            />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {additionalAssets.map((asset, index) => (
                <AdditionalAssetCard
                  key={asset.urlImage || index}
                  asset={asset}
                  index={index}
                  assetLabel={getAssetLabel(index)}
                  readOnly={readOnly}
                  onEdit={handleOpenEditModal}
                  onDelete={handleDeleteAsset}
                  onPreviewImage={onOpenPreviewModal}
                />
              ))}
              {!readOnly && !isMaxReached && (
                <AdditionalAssetAddSlot
                  onClick={triggerUpload}
                  isUploading={isUploading}
                />
              )}
            </Box>
          )}

          {!readOnly && (
            <UploadFileButton
              id={UPLOAD_INPUT_ID}
              isHidden
              disableVerificationMedia={true}
              onChange={onImageUpload}
              pathStorage={`socialMedia/${userID}/additionalAssets/`}
              acceptedFormats={LOGO_IMAGE_FORMATS}
              modeUpload="sync"
              loading={isUploading}
              onCallbackLoading={(loading) => setIsUploading(loading)}
            />
          )}
        </Box>
      </CardContainer>

      <ModalAdditionalAsset
        modalOpen={isModalOpen}
        onCloseModal={handleCloseModal}
        urlImage={localAsset.urlImage || pendingUrlImage}
        prompt={localAsset.prompt}
        isConditional={localAsset.isConditional}
        variableInfo={localAsset.variableInfo}
        variantGroup={localAsset.variantGroup}
        variantGroupOptions={variantGroupOptions}
        hasAdvancedConfigurationAdditionalAssets={
          hasAdvancedConfigurationAdditionalAssets
        }
        onSave={handleSaveAsset}
        onChangeImage={readOnly ? undefined : triggerUpload}
        isUploading={isUploading}
      />

      {isPreviewModalOpen && (
        <ModalMedia
          modalOpen={isPreviewModalOpen}
          onCloseModal={onClosePreviewModal}
          url={previewImageUrl}
          showEditButton={false}
          showShareButton={false}
          showDownloadButton={true}
          showDeleteButton={false}
        />
      )}
    </>
  );
};

export default AdditionalAssetsSection;
